import React from "react";
import { Check, X, ExternalLink } from "lucide-react";

/**
 * ITER-246: Legacy "Background check" block on the car ad detail page.
 * Four rows — Stolen / Damaged Import / Re-registered / Money Owing.
 *
 * Source priority (per spec §3):
 *   1. `listing.background_check` — structured `pass|fail|unknown` mirror
 *      attached to the detail payload when CarJam data is cached/mapped.
 *   2. Fallback to raw `attributes["Stolen Vehicle" | ...]` chips ("N"/"Y")
 *      so listings created before the backend mirror still render.
 *
 * When a row is `unknown`, we render an affiliate CTA chip that deep-links
 * to `carjam_report_url` (20% share requires referrer — `rel="noopener"`,
 * never noreferrer).
 */
const ROWS = [
  {
    key: "stolen",
    label: "Stolen Vehicle",
    attrKey: "Stolen Vehicle",
    passText: "NOT REPORTED STOLEN",
    failText: "REPORTED STOLEN",
  },
  {
    key: "money_owing",
    label: "Money Owing",
    attrKey: "Money Owing",
    passText: "NO MONEY OWING",
    failText: "MONEY OWING",
  },
  {
    key: "re_registered",
    label: "Re-registered Check",
    attrKey: "Re-registered Check",
    passText: "NOT RE-REGISTERED",
    failText: "RE-REGISTERED",
  },
  {
    key: "imported_damaged",
    label: "Damaged Import Check",
    attrKey: "Damaged Import Check",
    passText: "NOT A DAMAGED IMPORT",
    failText: "DAMAGED IMPORT",
  },
];

const statusFromAttr = (v) => {
  if (v === "N" || v === "No" || v === "no" || v === false) return "pass";
  if (v === "Y" || v === "Yes" || v === "yes" || v === true) return "fail";
  return "unknown";
};

function Chip({ status, passText, failText, reportUrl }) {
  if (status === "pass") {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
        data-testid="bgcheck-chip-pass"
      >
        <Check className="w-3.5 h-3.5" /> {passText}
      </span>
    );
  }
  if (status === "fail") {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
        data-testid="bgcheck-chip-fail"
      >
        <X className="w-3.5 h-3.5" /> {failText}
      </span>
    );
  }
  // unknown — grey affiliate CTA
  if (reportUrl) {
    return (
      // eslint-disable-next-line react/jsx-no-target-blank -- CarJam affiliate REQUIRES referrer for attribution (20% share). Never noreferrer.
      <a
        href={reportUrl}
        target="_blank"
        rel="noopener"
        data-testid="bgcheck-chip-unknown"
        className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)] px-3 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors"
      >
        CHECK ON CARJAM <ExternalLink className="w-3 h-3" />
      </a>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
      data-testid="bgcheck-chip-unknown"
    >
      UNKNOWN
    </span>
  );
}

export default function CarBackgroundCheck({ listing }) {
  const attrs = listing.attributes || {};
  const structured = listing.background_check || null;
  const reportUrl = listing.carjam_report_url || "";

  const rowStatus = (row) => {
    if (structured && structured[row.key]) {
      const s = String(structured[row.key]).toLowerCase();
      if (s === "pass" || s === "fail" || s === "unknown") return s;
    }
    if (row.attrKey in attrs) return statusFromAttr(attrs[row.attrKey]);
    return "unknown";
  };

  // Hide the whole block if there's no data at all (non-car ads or truly empty).
  const anyData =
    !!structured ||
    ROWS.some((r) => r.attrKey in attrs) ||
    !!reportUrl;
  if (!anyData) return null;

  return (
    <div
      data-testid="ad-background-check"
      className="mt-6 rounded-2xl bg-white border border-soft p-5"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-lg font-bold text-slate-800">
          Background check
        </h2>
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
          Data provided by CarJam
        </span>
      </div>
      <ul className="mt-3 divide-y divide-subtle">
        {ROWS.map((row) => {
          const status = rowStatus(row);
          return (
            <li
              key={row.key}
              className="py-2.5 flex items-center justify-between gap-3"
              data-testid={`bgcheck-row-${row.key}`}
            >
              <span className="text-sm font-semibold text-slate-700">
                {row.label}
              </span>
              <Chip
                status={status}
                passText={row.passText}
                failText={row.failText}
                reportUrl={reportUrl}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
